import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AuthService } from 'src/app/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { Router } from '@angular/router';
import { TicketSystemComponent } from '../ticket-system/ticket-system.component';
import * as bcrypt from 'bcryptjs';

declare var bootstrap: any;

@Component({
  selector: 'app-employee-preferences',
  templateUrl: './employee-preferences.component.html',
  styleUrls: ['./employee-preferences.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TicketSystemComponent,
],
})
export class EmployeePreferencesComponent implements OnInit {
  user: any;
  form!: FormGroup;
  showPin = false;
  showPassword = false;
  showToast = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  saving = false;
  loading = true;

  // Account setup status tracking
  hasEmail = false;
  hasPassword = false;
  hasPin = false;
  setupComplete = false;
  setupScore = 0;

  private personalModal: any;
  private securityModal: any;
  private supportModal: any;

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private firebaseService: FirebaseService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.user = this.auth.getUser();
    if (!this.user || !this.user.id) {
      this.router.navigate(['/login']);
      return;
    }

    this.initializeForm();
    await this.loadEmployeeData();

    setTimeout(() => {
      this.initializeModals();
    }, 100);
  }

  private initializeModals(): void {
    const personalModalEl = document.getElementById('personalModal');
    const securityModalEl = document.getElementById('securityModal');
    const supportModalEl = document.getElementById('supportModal');

    if (personalModalEl) {
      this.personalModal = new bootstrap.Modal(personalModalEl);
    }
    if (securityModalEl) {
      this.securityModal = new bootstrap.Modal(securityModalEl);
    }
    if (supportModalEl) {
      this.supportModal = new bootstrap.Modal(supportModalEl);
    }
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      newPassword: ['', [Validators.minLength(6)]],
      pin: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]+$/),
        ],
      ],
    });
  }

  private async loadEmployeeData(): Promise<void> {
    this.loading = true;
    try {
      const employeeData: any = await this.firebaseService.getEmployeeById(
        this.user.id
      );

      if (employeeData) {
        this.user = employeeData;

        // Check setup status
        this.hasEmail = !!(
          employeeData.email && employeeData.email.includes('@')
        );
        this.hasPassword = !!(
          employeeData.password && employeeData.password.length > 0
        );
        this.hasPin = !!(employeeData.Pin && employeeData.Pin.length >= 4);

        // Calculate setup score
        this.setupScore = 0;
        if (this.hasEmail) this.setupScore += 33;
        if (this.hasPassword) this.setupScore += 33;
        if (this.hasPin) this.setupScore += 34;

        this.setupComplete = this.hasEmail && this.hasPassword && this.hasPin;

        this.form.patchValue({
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          email: employeeData.email || '',
          pin: employeeData.Pin || '',
        });

        // console.log('✅ Employee data loaded successfully');
      } else {
        this.displayToast('Could not load employee data.', 'error');
      }
    } catch (error) {
      console.error('❌ Error loading employee data:', error);
      this.displayToast('Error loading profile data.', 'error');
    } finally {
      this.loading = false;
    }
  }

  openModal(type: 'personal' | 'security' | 'support'): void {
    switch (type) {
      case 'personal':
        this.personalModal?.show();
        break;
      case 'security':
        this.securityModal?.show();
        break;
      case 'support':
        const modal = document.getElementById('ticketModal');
        if (modal) {
          const bsModal = new bootstrap.Modal(modal);
          bsModal.show();
        }
        break;
    }
  }

  private closeAllModals(): void {
    if (this.personalModal) {
      this.personalModal.hide();
    }
    if (this.securityModal) {
      this.securityModal.hide();
    }
    if (this.supportModal) {
      this.supportModal.hide();
    }
  }

  async savePersonalInfo(): Promise<void> {
    const firstNameControl = this.form.get('firstName');
    const lastNameControl = this.form.get('lastName');
    const emailControl = this.form.get('email');

    if (
      firstNameControl?.invalid ||
      lastNameControl?.invalid ||
      emailControl?.invalid
    ) {
      this.displayToast(
        'Please fill in all required fields correctly.',
        'error'
      );
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    try {
      const updateData: any = {
        firstName: firstNameControl?.value.trim(),
        lastName: lastNameControl?.value.trim(),
        email: emailControl?.value.trim().toLowerCase(),
      };

      await this.firebaseService.updateEmployeeProfile(
        this.user.id,
        updateData
      );

      const updatedUser = { ...this.user, ...updateData };
      await this.auth.updateUser(updatedUser);
      this.user = this.auth.getUser();

      // Reload to update setup status
      await this.loadEmployeeData();

      this.displayToast(
        'Personal information updated successfully!',
        'success'
      );
      this.closeAllModals();
    } catch (err) {
      console.error('Save error:', err);
      this.displayToast(
        'Error updating information. Please try again.',
        'error'
      );
    } finally {
      this.saving = false;
    }
  }

  async saveSecurityInfo(): Promise<void> {
    const pinControl = this.form.get('pin');
    const passwordControl = this.form.get('newPassword');

    if (pinControl?.invalid) {
      this.displayToast('Please enter a valid PIN.', 'error');
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    try {
      const updateData: any = {
        Pin: pinControl?.value,
      };

      // Only update password if user entered a new one
      const newPassword = passwordControl?.value?.trim();
      if (newPassword) {
        const salt = await bcrypt.genSalt(10);
        const hashedPassword = await bcrypt.hash(newPassword, salt);

        updateData.password = hashedPassword;
      }

      await this.firebaseService.updateEmployeeProfile(
        this.user.id,
        updateData
      );

      const updatedUser = { ...this.user, ...updateData };
      await this.auth.updateUser(updatedUser);
      this.user = this.auth.getUser();

      // Clear password field after successful save
      this.form.patchValue({ newPassword: '' });

      // Reload to update setup status
      await this.loadEmployeeData();

      this.displayToast('Security settings updated successfully!', 'success');
      this.closeAllModals();
    } catch (err) {
      console.error('Save error:', err);
      this.displayToast(
        'Error updating security settings. Please try again.',
        'error'
      );
    } finally {
      this.saving = false;
    }
  }

  toggleShowPin(): void {
    this.showPin = !this.showPin;
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  displayToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
    setTimeout(() => (this.showToast = false), 4000);
  }

  closeToast(): void {
    this.showToast = false;
  }

  getDisplayName(): string {
    if (this.user?.firstName && this.user?.lastName) {
      return `${this.user.firstName} ${this.user.lastName}`;
    }
    return this.user?.Name || 'Employee';
  }

  getSetupStatusText(): string {
    if (this.setupComplete) {
      return 'Account fully secured';
    }
    if (this.setupScore >= 66) {
      return 'Almost there!';
    }
    if (this.setupScore >= 33) {
      return 'Getting started';
    }
    return 'Setup incomplete';
  }

  getSetupStatusColor(): string {
    if (this.setupComplete) return 'success';
    if (this.setupScore >= 66) return 'warning';
    return 'danger';
  }

  navigateToDocumentation(): void {
    this.router.navigate(['/documentation']);
  }

}
